import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { adminApiClient } from "../../services/apiClient";
import { toApiError } from "../../types/api";

import type { Text } from "../../services/api/types";
import type { AdminTextFilters, PaginationInfo } from "../../types/api";

// Narrow types to replace previously untyped 'any' usages
interface TextStatisticsSummary {
  wordCount?: number;
  characterCount?: number;
  averageSentenceLength?: number;
  uniqueWords?: number;
  [key: string]: number | undefined; // fallback for backend extensions
}

interface CreateTextResponse {
  text: Text;
  generatedTranslations?: Array<{ language: string; translation: string }>; // optional if backend auto-generates
}

interface GenerateTranslationsResponse {
  translations: Array<{ language: string; translation: string }>; // aligns with typical translation response
  skippedLanguages?: string[];
}

interface BulkUpdateTextsResponse {
  updated: number;
  failed?: number;
}

interface BulkGenerateTranslationsResponse {
  requested?: number;
  generated?: number;
  skipped?: number;
  details?: Array<{
    textId: string;
    language: string;
    status: string;
    reason?: string;
  }>; // status per item
}

interface CsvInputRow {
  Level?: string;
  TextSize?: string;
  UserType?: string;
  ContentPrompt?: string;
  ExpectedTitle?: string;
  Notes?: string;
  [key: string]: string | undefined;
}

interface BulkGenerateFromFileResponse {
  status: string; // e.g., "completed" | "in-progress"
  totalTextsToGenerate?: number;
  allDuplicates?: boolean;
  skippedPrompts?: { prompt: string; reason: string; languages: string[] }[];
}

interface BulkGenerationStatusResponse {
  isActive: boolean;
  totalTexts: number;
  generatedTexts: number;
  currentLanguage: string | null;
  currentPrompt: string | null;
  errors?: string[];
  skippedPrompts?: { prompt: string; reason: string; languages: string[] }[];
}

interface BulkDeleteFilter {
  language?: string;
  difficulty?: string;
  access?: string;
  author?: string;
  search?: string;
  isPublic?: boolean;
  freeUserAccess?: boolean;
  moderation?: string;
}

interface BulkDeleteResponse {
  deleted?: number;
  count?: number;
  dryRun?: boolean;
}

interface AdminTextsState {
  texts: Text[];
  languages: Array<{ code: string; name: string; count: number }>;
  loading: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  filters: AdminTextFilters;
  selectedText: Text | null;
  statistics: Record<string, TextStatisticsSummary>;
  bulkGenerationProgress: {
    isActive: boolean;
    totalTexts: number;
    generatedTexts: number;
    currentLanguage: string | null;
    currentPrompt: string | null;
    errors: string[];
    skippedPrompts?: { prompt: string; reason: string; languages: string[] }[];
  } | null;
}

const initialState: AdminTextsState = {
  texts: [],
  languages: [],
  loading: false,
  error: null,
  pagination: null,
  filters: {},
  selectedText: null,
  statistics: {},
  bulkGenerationProgress: null,
};

// Async thunks
export const fetchAdminTexts = createAsyncThunk<
  { texts: Text[]; pagination: PaginationInfo },
  {
    page?: number;
    limit?: number;
    search?: string;
    language?: string;
    difficulty?: string;
    access?: string;
    moderation?: string;
    author?: string;
  },
  { rejectValue: string }
>("adminTexts/fetchTexts", async (params = {}, { rejectWithValue }) => {
  try {
    const response = await adminApiClient.get("/admin/texts", {
      params: {
        page: params.page || 1,
        limit: params.limit || 20,
        search: params.search,
        language: params.language,
        difficulty: params.difficulty,
        access: params.access,
        moderation: params.moderation,
        author: params.author,
      },
    });
    return response.data;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const fetchTextLanguages = createAsyncThunk<
  Array<{ code: string; name: string; count: number }>,
  void,
  { rejectValue: string }
>("adminTexts/fetchTextLanguages", async (_, { rejectWithValue }) => {
  try {
    const response = await adminApiClient.get("/admin/texts/languages");
    return response.data.languages;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const fetchTextById = createAsyncThunk<
  Text,
  string,
  { rejectValue: string }
>("adminTexts/fetchTextById", async (textId, { rejectWithValue }) => {
  try {
    const response = await adminApiClient.get(`/admin/texts/${textId}`);
    return response.data.text;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const fetchTextStatistics = createAsyncThunk<
  TextStatisticsSummary,
  string,
  { rejectValue: string }
>("adminTexts/fetchTextStatistics", async (textId, { rejectWithValue }) => {
  try {
    const response = await adminApiClient.get(
      `/admin/texts/${textId}/statistics`,
    );
    return response.data;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const createText = createAsyncThunk<
  CreateTextResponse,
  {
    contentPrompt: string;
    level: string;
    languages: string[];
    textSize: string;
    freeUserAccess: boolean;
    isPublic: boolean;
  },
  { rejectValue: string }
>("adminTexts/createText", async (data, { rejectWithValue }) => {
  try {
    const response = await adminApiClient.post("/admin/texts/create", data);
    return response.data;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const updateText = createAsyncThunk<
  Text,
  { id: string; data: Partial<Text> },
  { rejectValue: string }
>("adminTexts/updateText", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await adminApiClient.put(`/admin/texts/${id}`, data);
    return response.data.text;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const generateTextTranslations = createAsyncThunk<
  GenerateTranslationsResponse,
  { textId: string; languages: string[] },
  { rejectValue: string }
>(
  "adminTexts/generateTextTranslations",
  async ({ textId, languages }, { rejectWithValue }) => {
    try {
      const response = await adminApiClient.post(
        `/admin/texts/${textId}/translations`,
        {
          languages,
        },
      );
      return response.data;
    } catch (error: unknown) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

export const bulkUpdateTexts = createAsyncThunk<
  BulkUpdateTextsResponse,
  { textIds: string[]; updates: Partial<Text> },
  { rejectValue: string }
>(
  "adminTexts/bulkUpdateTexts",
  async ({ textIds, updates }, { rejectWithValue }) => {
    try {
      const response = await adminApiClient.patch("/admin/texts/bulk", {
        textIds,
        updates,
      });
      return response.data;
    } catch (error: unknown) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

export const generateBulkTranslations = createAsyncThunk<
  BulkGenerateTranslationsResponse,
  {
    textIds?: string[];
    languages?: string[];
    fromLanguage?: string;
    toLanguage?: string;
    difficulties?: string[];
    limit?: number;
  },
  { rejectValue: string }
>(
  "adminTexts/generateBulkTranslations",
  async (params, { rejectWithValue }) => {
    try {
      // Определяем какой endpoint использовать
      if (params.fromLanguage && params.toLanguage && params.difficulties) {
        // Новый расширенный endpoint
        const response = await adminApiClient.post(
          "/admin/texts/bulk-generate-translations",
          {
            fromLanguage: params.fromLanguage,
            toLanguage: params.toLanguage,
            difficulties: params.difficulties,
            limit: params.limit || 50,
          },
        );
        return response.data;
      } else {
        // Старый endpoint для выбранных текстов
        const response = await adminApiClient.post(
          "/admin/texts/bulk-translations",
          {
            textIds: params.textIds,
            languages: params.languages,
          },
        );
        return response.data;
      }
    } catch (error: unknown) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

export const bulkGenerateFromFile = createAsyncThunk<
  BulkGenerateFromFileResponse,
  {
    csvData: CsvInputRow[];
    languages: string[];
    checkDuplicates?: boolean;
    maxTextsPerBatch?: number;
  },
  { rejectValue: string }
>("adminTexts/bulkGenerateFromFile", async (params, { rejectWithValue }) => {
  try {
    const response = await adminApiClient.post(
      "/admin/texts/bulk-generate-from-file",
      {
        csvData: params.csvData,
        languages: params.languages,
        checkDuplicates: params.checkDuplicates,
        maxTextsPerBatch: params.maxTextsPerBatch,
      },
    );

    return response.data;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const fetchBulkGenerationStatus = createAsyncThunk<
  BulkGenerationStatusResponse,
  void,
  { rejectValue: string }
>("adminTexts/fetchBulkGenerationStatus", async (_, { rejectWithValue }) => {
  try {
    const response = await adminApiClient.get(
      "/admin/texts/bulk-generation-status",
    );
    return response.data;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const deleteText = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("adminTexts/deleteText", async (textId, { rejectWithValue }) => {
  try {
    await adminApiClient.delete(`/admin/texts/${textId}`);
    return textId;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

// Массовое удаление по фильтрам (двухфазно: dryRun для подтверждения)
export const bulkDeleteTexts = createAsyncThunk<
  BulkDeleteResponse,
  { filters: BulkDeleteFilter; dryRun?: boolean; confirmPhrase?: string },
  { rejectValue: string }
>(
  "adminTexts/bulkDeleteTexts",
  async ({ filters, dryRun, confirmPhrase }, { rejectWithValue }) => {
    try {
      const response = await adminApiClient.post("/admin/texts/bulk-delete", {
        filters,
        dryRun: !!dryRun,
        confirmPhrase,
      });
      return response.data;
    } catch (error: unknown) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

// Физическое (hard) удаление по фильтрам
export const bulkHardDeleteTexts = createAsyncThunk<
  BulkDeleteResponse,
  {
    filters: BulkDeleteFilter;
    dryRun?: boolean;
    confirmPhrase?: string;
    includeLogs?: boolean;
  },
  { rejectValue: string }
>(
  "adminTexts/bulkHardDeleteTexts",
  async (
    { filters, dryRun, confirmPhrase, includeLogs },
    { rejectWithValue },
  ) => {
    try {
      const response = await adminApiClient.post("/admin/texts/hard-delete", {
        filters,
        dryRun: !!dryRun,
        confirmPhrase,
        includeLogs,
      });
      return response.data;
    } catch (error: unknown) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

export const adminTextsSlice = createSlice({
  name: "adminTexts",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    setSelectedText: (state, action) => {
      state.selectedText = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedText: (state) => {
      state.selectedText = null;
    },
    setBulkGenerationProgress: (state, action) => {
      state.bulkGenerationProgress = action.payload;
    },
    updateBulkGenerationProgress: (state, action) => {
      if (state.bulkGenerationProgress) {
        state.bulkGenerationProgress = {
          ...state.bulkGenerationProgress,
          ...action.payload,
        };
      }
    },
    clearBulkGenerationProgress: (state) => {
      state.bulkGenerationProgress = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminTexts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminTexts.fulfilled, (state, action) => {
        state.loading = false;
        state.texts = action.payload.texts;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchAdminTexts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTextLanguages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTextLanguages.fulfilled, (state, action) => {
        state.loading = false;
        state.languages = action.payload;
      })
      .addCase(fetchTextLanguages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTextById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTextById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedText = action.payload;
      })
      .addCase(fetchTextById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTextStatistics.fulfilled, (state, action) => {
        state.statistics[action.meta.arg] = action.payload;
      })
      .addCase(createText.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createText.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createText.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateText.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateText.fulfilled, (state, action) => {
        state.loading = false;
        // Update text in the list if it exists
        const index = state.texts.findIndex(
          (text) => text.id === action.payload.id,
        );
        if (index !== -1) {
          state.texts[index] = action.payload;
        }
        // Update selected text if it's the same
        if (state.selectedText?.id === action.payload.id) {
          state.selectedText = action.payload;
        }
      })
      .addCase(updateText.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(generateTextTranslations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateTextTranslations.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(generateTextTranslations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteText.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteText.fulfilled, (state, action) => {
        state.loading = false;
        // Remove text from the list
        state.texts = state.texts.filter((text) => text.id !== action.payload);
        // Clear selected text if it was deleted
        if (state.selectedText?.id === action.payload) {
          state.selectedText = null;
        }
      })
      .addCase(deleteText.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(bulkDeleteTexts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeleteTexts.fulfilled, (state) => {
        state.loading = false;
        // После реального удаления фронт отдельно перезагружает список
      })
      .addCase(bulkDeleteTexts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(bulkHardDeleteTexts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkHardDeleteTexts.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(bulkHardDeleteTexts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(bulkGenerateFromFile.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.bulkGenerationProgress = {
          isActive: true,
          totalTexts: 0,
          generatedTexts: 0,
          currentLanguage: null,
          currentPrompt: null,
          errors: [],
        };
      })
      .addCase(bulkGenerateFromFile.fulfilled, (state, action) => {
        state.loading = false;
        if (state.bulkGenerationProgress) {
          state.bulkGenerationProgress = {
            ...state.bulkGenerationProgress,
            isActive: action.payload.status !== "completed", // Если статус completed - генерация не активна
            totalTexts: action.payload.totalTextsToGenerate || 0,
            skippedPrompts: action.payload.skippedPrompts || [],
          };
        }

        // Если все тексты оказались дубликатами (allDuplicates: true), устанавливаем финальное состояние
        if (action.payload.allDuplicates === true) {
          state.bulkGenerationProgress = {
            isActive: false,
            totalTexts: action.payload.totalTextsToGenerate || 0,
            generatedTexts: 0, // Ничего не сгенерировано
            currentLanguage: null,
            currentPrompt: null,
            errors: [],
            skippedPrompts: action.payload.skippedPrompts || [],
          };
        }
      })
      .addCase(fetchBulkGenerationStatus.fulfilled, (state, action) => {
        // Обновляем статус на основе данных с сервера
        if (action.payload.isActive || state.bulkGenerationProgress) {
          state.bulkGenerationProgress = {
            isActive: action.payload.isActive,
            totalTexts: action.payload.totalTexts,
            generatedTexts: action.payload.generatedTexts,
            currentLanguage: action.payload.currentLanguage,
            currentPrompt: action.payload.currentPrompt,
            errors: action.payload.errors || [],
            skippedPrompts:
              action.payload.skippedPrompts ||
              state.bulkGenerationProgress?.skippedPrompts ||
              [],
          };
        }
      })
      .addCase(bulkGenerateFromFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        if (state.bulkGenerationProgress) {
          state.bulkGenerationProgress.isActive = false;
        }
      });
  },
});

export const {
  setFilters,
  clearError,
  setSelectedText,
  clearSelectedText,
  setBulkGenerationProgress,
  updateBulkGenerationProgress,
  clearBulkGenerationProgress,
} = adminTextsSlice.actions;

// Export with alias for compatibility
export { deleteText as deleteAdminText };

export default adminTextsSlice.reducer;
